import React, { useEffect, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { UserProfile, UserPreferences, KoboAccount, KoboAccountFormData } from '../types';
import { apiService } from '../services/api';

const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    organization: '',
    contact_details: {
      phone: '',
      address: ''
    }
  });
  
  // Preferences form state
  const [preferencesForm, setPreferencesForm] = useState<UserPreferences>({
    notifications: {
      email_enabled: true,
      sms_enabled: false,
      push_enabled: true,
      frequency: 'daily'
    },
    ui: {
      theme: 'light',
      language: 'en'
    },
    project_defaults: {
      auto_save_interval: 300
    }
  });
  
  // KoboToolbox account management state
  const [koboAccounts, setKoboAccounts] = useState<KoboAccount[]>([]);
  const [showKoboDialog, setShowKoboDialog] = useState(false);
  const [editingKoboAccount, setEditingKoboAccount] = useState<KoboAccount | null>(null);
  const [koboForm, setKoboForm] = useState<KoboAccountFormData>({
    account_name: '',
    api_token: '',
    server_url: 'https://kf.kobotoolbox.org/api/v2'
  });
  const [validatingKobo, setValidatingKobo] = useState<string | null>(null);
  
  const toast = React.useRef<Toast>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUserProfile();
      
      if (response.success && response.data) {
        const profileData = response.data;
        setProfile(profileData);
        
        // Update form states
        setProfileForm({
          name: profileData.name || '',
          organization: profileData.organization || '',
          contact_details: {
            phone: profileData.contact_details?.phone || '',
            address: profileData.contact_details?.address || ''
          }
        });
        
        setPreferencesForm({
          ...preferencesForm,
          ...profileData.preferences
        });
        
        setKoboAccounts(profileData.kobo_accounts || []);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to load profile'
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load profile'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    try {
      setSaving(true);
      const response = await apiService.updateUserProfile(profileForm);
      
      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Profile updated successfully'
        });
        loadProfile(); // Reload to get updated data
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to update profile'
        });
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update profile'
      });
    } finally {
      setSaving(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const response = await apiService.updateUserPreferences(preferencesForm);
      
      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Preferences updated successfully'
        });
        loadProfile(); // Reload to get updated data
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to update preferences'
        });
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update preferences'
      });
    } finally {
      setSaving(false);
    }
  };

  const openKoboDialog = (account?: KoboAccount) => {
    if (account) {
      setEditingKoboAccount(account);
      setKoboForm({
        account_name: account.account_name,
        api_token: '', // Don't pre-fill token for security
        server_url: account.server_url
      });
    } else {
      setEditingKoboAccount(null);
      setKoboForm({
        account_name: '',
        api_token: '',
        server_url: 'https://kf.kobotoolbox.org/api/v2'
      });
    }
    setShowKoboDialog(true);
  };

  const saveKoboAccount = async () => {
    try {
      setSaving(true);
      
      let response;
      if (editingKoboAccount) {
        response = await apiService.updateKoboAccount(editingKoboAccount.account_id, koboForm);
      } else {
        response = await apiService.addKoboAccount(koboForm);
      }
      
      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: editingKoboAccount ? 'Account updated successfully' : 'Account added successfully'
        });
        setShowKoboDialog(false);
        loadProfile(); // Reload to get updated accounts
      } else {
        let errorMessage = response.error || 'Failed to save account';
        if (response.validation_errors && response.validation_errors.length > 0) {
          errorMessage += ': ' + response.validation_errors.join(', ');
        }
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: errorMessage
        });
      }
    } catch (error) {
      console.error('Failed to save KoboToolbox account:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to save account'
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteKoboAccount = (account: KoboAccount) => {
    confirmDialog({
      message: `Are you sure you want to delete the account "${account.account_name}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          const response = await apiService.deleteKoboAccount(account.account_id);
          
          if (response.success) {
            toast.current?.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Account deleted successfully'
            });
            loadProfile(); // Reload to get updated accounts
          } else {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: response.error || 'Failed to delete account'
            });
          }
        } catch (error) {
          console.error('Failed to delete KoboToolbox account:', error);
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to delete account'
          });
        }
      }
    });
  };

  const validateKoboAccount = async (account: KoboAccount) => {
    try {
      setValidatingKobo(account.account_id);
      const response = await apiService.validateKoboAccount(account.account_id);
      
      if (response.success && response.data?.validation_result?.valid) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Account validation successful'
        });
        loadProfile(); // Reload to get updated validation status
      } else {
        const errorMessage = response.data?.validation_result?.error || response.error || 'Validation failed';
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Failed',
          detail: errorMessage
        });
      }
    } catch (error) {
      console.error('Failed to validate KoboToolbox account:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to validate account'
      });
    } finally {
      setValidatingKobo(null);
    }
  };

  const setDefaultKoboAccount = async (account: KoboAccount) => {
    try {
      const response = await apiService.setDefaultKoboAccount(account.account_id);
      
      if (response.success) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Default account updated successfully'
        });
        loadProfile(); // Reload to get updated default status
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: response.error || 'Failed to set default account'
        });
      }
    } catch (error) {
      console.error('Failed to set default KoboToolbox account:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to set default account'
      });
    }
  };

  const koboAccountActions = (account: KoboAccount) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <Button
        icon="pi pi-check"
        size="small"
        text
        tooltip="Validate"
        loading={validatingKobo === account.account_id}
        onClick={() => validateKoboAccount(account)}
      />
      <Button
        icon="pi pi-star"
        size="small"
        text
        tooltip="Set as Default"
        onClick={() => setDefaultKoboAccount(account)}
      />
      <Button
        icon="pi pi-pencil"
        size="small"
        text
        tooltip="Edit"
        onClick={() => openKoboDialog(account)}
      />
      <Button
        icon="pi pi-trash"
        size="small"
        text
        severity="danger"
        tooltip="Delete"
        onClick={() => deleteKoboAccount(account)}
      />
    </div>
  );

  const koboAccountStatus = (account: KoboAccount) => {
    const isDefault = profile?.default_kobo_account?.account_id === account.account_id;
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        {account.is_active ? (
          <Tag value="Active" severity="success" />
        ) : (
          <Tag value="Inactive" severity="danger" />
        )}
        {isDefault && <Tag value="Default" severity="info" />}
      </div>
    );
  };

  const frequencyOptions = [
    { label: 'Immediate', value: 'immediate' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Never', value: 'never' }
  ];

  const themeOptions = [
    { label: 'Light', value: 'light' },
    { label: 'Dark', value: 'dark' },
    { label: 'Auto', value: 'auto' }
  ];

  const languageOptions = [
    { label: 'English', value: 'en' },
    { label: 'Arabic', value: 'ar' },
    { label: 'French', value: 'fr' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#3B82F6' }}></i>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Toast ref={toast} />
      <ConfirmDialog />
      
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.75rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>Profile Settings</h1>
        <p style={{ color: '#6b7280', margin: 0 }}>Manage your account information, preferences, and KoboToolbox integrations</p>
      </div>

      <Card>
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          {/* Profile Information Tab */}
          <TabPanel header={<span><i className="pi pi-user" style={{ marginRight: '0.5rem' }}></i>Profile Information</span>}>
            <div style={{ maxWidth: '600px' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Full Name *
                </label>
                <InputText
                  id="name"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Email Address
                </label>
                <InputText
                  id="email"
                  value={profile?.email || ''}
                  disabled
                  style={{ width: '100%' }}
                />
                <small style={{ color: '#6b7280' }}>Email cannot be changed</small>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="organization" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Organization
                </label>
                <InputText
                  id="organization"
                  value={profileForm.organization}
                  onChange={(e) => setProfileForm({ ...profileForm, organization: e.target.value })}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="phone" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Phone Number
                </label>
                <InputText
                  id="phone"
                  value={profileForm.contact_details.phone}
                  onChange={(e) => setProfileForm({
                    ...profileForm,
                    contact_details: { ...profileForm.contact_details, phone: e.target.value }
                  })}
                  style={{ width: '100%' }}
                />
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label htmlFor="address" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Address
                </label>
                <InputTextarea
                  id="address"
                  value={profileForm.contact_details.address}
                  onChange={(e) => setProfileForm({
                    ...profileForm,
                    contact_details: { ...profileForm.contact_details, address: e.target.value }
                  })}
                  rows={3}
                  style={{ width: '100%' }}
                />
              </div>

              <Button
                label="Save Profile"
                icon="pi pi-save"
                loading={saving}
                onClick={saveProfile}
              />
            </div>
          </TabPanel>

          {/* Preferences Tab */}
          <TabPanel header={<span><i className="pi pi-cog" style={{ marginRight: '0.5rem' }}></i>Preferences</span>}>
            <div style={{ maxWidth: '600px' }}>
              <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Notifications</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <InputSwitch
                    checked={preferencesForm.notifications?.email_enabled || false}
                    onChange={(e) => setPreferencesForm({
                      ...preferencesForm,
                      notifications: { ...preferencesForm.notifications, email_enabled: e.value }
                    })}
                  />
                  <label>Email Notifications</label>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <InputSwitch
                    checked={preferencesForm.notifications?.push_enabled || false}
                    onChange={(e) => setPreferencesForm({
                      ...preferencesForm,
                      notifications: { ...preferencesForm.notifications, push_enabled: e.value }
                    })}
                  />
                  <label>Push Notifications</label>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Notification Frequency
                </label>
                <Dropdown
                  value={preferencesForm.notifications?.frequency || 'daily'}
                  options={frequencyOptions}
                  onChange={(e) => setPreferencesForm({
                    ...preferencesForm,
                    notifications: { ...preferencesForm.notifications, frequency: e.value }
                  })}
                  style={{ width: '200px' }}
                />
              </div>

              <Divider />

              <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>User Interface</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Theme
                  </label>
                  <Dropdown
                    value={preferencesForm.ui?.theme || 'light'}
                    options={themeOptions}
                    onChange={(e) => setPreferencesForm({
                      ...preferencesForm,
                      ui: { ...preferencesForm.ui, theme: e.value }
                    })}
                    style={{ width: '100%' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Language
                  </label>
                  <Dropdown
                    value={preferencesForm.ui?.language || 'en'}
                    options={languageOptions}
                    onChange={(e) => setPreferencesForm({
                      ...preferencesForm,
                      ui: { ...preferencesForm.ui, language: e.value }
                    })}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              <Divider />

              <h3 style={{ marginBottom: '1rem', color: '#1f2937' }}>Project Defaults</h3>
              
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  Auto-save Interval (seconds)
                </label>
                <InputText
                  type="number"
                  value={preferencesForm.project_defaults?.auto_save_interval?.toString() || '300'}
                  onChange={(e) => setPreferencesForm({
                    ...preferencesForm,
                    project_defaults: { 
                      ...preferencesForm.project_defaults, 
                      auto_save_interval: parseInt(e.target.value) || 300 
                    }
                  })}
                  style={{ width: '200px' }}
                />
              </div>

              <Button
                label="Save Preferences"
                icon="pi pi-save"
                loading={saving}
                onClick={savePreferences}
              />
            </div>
          </TabPanel>

          {/* KoboToolbox Accounts Tab */}
          <TabPanel header={<span><i className="pi pi-database" style={{ marginRight: '0.5rem' }}></i>KoboToolbox Accounts</span>}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', color: '#1f2937' }}>KoboToolbox Accounts</h3>
                  <p style={{ color: '#6b7280', margin: 0 }}>Manage your KoboToolbox account integrations for survey deployment</p>
                </div>
                <Button
                  label="Add Account"
                  icon="pi pi-plus"
                  onClick={() => openKoboDialog()}
                />
              </div>

              {koboAccounts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <i className="pi pi-database" style={{ fontSize: '3rem', color: '#d1d5db', marginBottom: '1rem', display: 'block' }}></i>
                  <div style={{ fontSize: '1.25rem', fontWeight: 500, color: '#1f2937', marginBottom: '0.5rem' }}>No KoboToolbox accounts</div>
                  <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Add your KoboToolbox account to deploy surveys</p>
                  <Button
                    label="Add Account"
                    icon="pi pi-plus"
                    onClick={() => openKoboDialog()}
                  />
                </div>
              ) : (
                <DataTable value={koboAccounts}>
                  <Column field="account_name" header="Account Name" />
                  <Column field="server_url" header="Server URL" />
                  <Column field="account_info.username" header="Username" />
                  <Column body={koboAccountStatus} header="Status" />
                  <Column body={koboAccountActions} header="Actions" style={{ width: '12rem' }} />
                </DataTable>
              )}
            </div>
          </TabPanel>
        </TabView>
      </Card>

      {/* KoboToolbox Account Dialog */}
      <Dialog
        header={editingKoboAccount ? 'Edit KoboToolbox Account' : 'Add KoboToolbox Account'}
        visible={showKoboDialog}
        onHide={() => setShowKoboDialog(false)}
        style={{ width: '500px' }}
        modal
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="account_name" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Account Name *
            </label>
            <InputText
              id="account_name"
              value={koboForm.account_name}
              onChange={(e) => setKoboForm({ ...koboForm, account_name: e.target.value })}
              style={{ width: '100%' }}
              placeholder="My KoboToolbox Account"
            />
          </div>

          <div>
            <label htmlFor="server_url" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Server URL *
            </label>
            <InputText
              id="server_url"
              value={koboForm.server_url}
              onChange={(e) => setKoboForm({ ...koboForm, server_url: e.target.value })}
              style={{ width: '100%' }}
              placeholder="https://kf.kobotoolbox.org/api/v2"
            />
          </div>

          <div>
            <label htmlFor="api_token" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              API Token *
            </label>
            <InputText
              id="api_token"
              type="password"
              value={koboForm.api_token}
              onChange={(e) => setKoboForm({ ...koboForm, api_token: e.target.value })}
              style={{ width: '100%' }}
              placeholder="Your KoboToolbox API token"
            />
            <small style={{ color: '#6b7280' }}>
              You can find your API token in your KoboToolbox account settings
            </small>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
            <Button
              label="Cancel"
              text
              onClick={() => setShowKoboDialog(false)}
            />
            <Button
              label={editingKoboAccount ? 'Update' : 'Add'}
              icon="pi pi-save"
              loading={saving}
              onClick={saveKoboAccount}
              disabled={!koboForm.account_name || !koboForm.api_token || !koboForm.server_url}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ProfilePage;